// Silueta lateral compacta de una nave para la pantalla Inicio.
// La variante compacta reproduce la tarjeta de referencia: lecturas T/H
// arriba, nave baja a la izquierda y ambiente en una columna independiente.

import { TEMP_MAX_OK, TEMP_MIN_OK } from '@/lib/umbrales';
import { fmt1 } from '@/lib/format';

const COMPACT = {
  viewBox: '0 0 220 64',
  wallLeft: 8,
  wallRight: 194,
  endRight: 212,
  eaveY: 34,
  ridgeLeft: 24,
  ridgeRight: 194,
  ridgeY: 12,
  floorY: 60,
  sensorX: [37, 110, 183],
  sensorY: 47,
};

const FULL = {
  wallLeft: 12,
  wallRight: 168,
  endRight: 192,
  eaveY: 44,
  ridgeLeft: 32,
  ridgeRight: 168,
  ridgeY: 14,
  floorY: 104,
  sensorX: [52, 110, 168],
  sensorY: 74,
};

export interface SensorLectura {
  station?: string;
  /** Temperatura en °C o null si no hay lectura. */
  temp: number | null;
  /** Humedad en % o null si no hay lectura. */
  hum: number | null;
  /** Estado de la temperatura reportado por la API. */
  tempState?: 'normal' | 'alert' | 'disconnected' | 'no_data';
  /** Estado de la humedad reportado por la API; independiente del de temperatura. */
  humState?: 'normal' | 'alert' | 'disconnected' | 'no_data';
}

interface SiluetaNaveProps {
  /** Tres lecturas, una por sensor. */
  sensores: SensorLectura[];
  /** Variante baja usada en las tarjetas de la Home. */
  compacta?: boolean;
  /** Dibujo sin lecturas para las filas compactas de Inicio. */
  mini?: boolean;
  /** Umbrales provisionales de temperatura. */
  tempMinOk?: number;
  tempMaxOk?: number;
}

function colorValor(
  value: number | null,
  state: SensorLectura['tempState'],
  tempMinOk: number,
  tempMaxOk: number,
): string {
  if (state === 'disconnected' || state === 'no_data') return 'var(--c-border-strong)';
  if (state === 'alert' || (value != null && (value < tempMinOk || value > tempMaxOk))) {
    return 'var(--c-warning-line)';
  }
  if (value != null) return 'var(--c-ok-dot)';
  return 'var(--c-border-strong)';
}

function colorSensor(sensor: SensorLectura, tempMinOk: number, tempMaxOk: number): string {
  // El punto del sensor usa el peor estado entre temperatura y humedad,
  // pero cada valor numérico se colorea por su propio estado.
  const peor = (a?: SensorLectura['tempState'], b?: SensorLectura['tempState']) =>
    a === 'alert' || b === 'alert'
      ? 'alert'
      : a === 'disconnected' || b === 'disconnected'
        ? 'disconnected'
        : (a ?? b);
  return colorValor(sensor.temp, peor(sensor.tempState, sensor.humState), tempMinOk, tempMaxOk);
}

export function SiluetaNave({
  sensores,
  compacta = false,
  mini = false,
  tempMinOk = TEMP_MIN_OK,
  tempMaxOk = TEMP_MAX_OK,
}: SiluetaNaveProps) {
  const standard =
    sensores.length <= 3 &&
    sensores.every((sensor) => !sensor.station || /^[ABC]$/.test(sensor.station));
  if (mini) {
    return (
      <svg viewBox="0 0 68 34" className="h-[34px] w-full" aria-hidden>
        <image href="/images/nave-cubierta.webp" width={68} height={34} />
        {sensores.map((sensor, index) => (
          <circle
            key={`sensor-${index}`}
            cx={
              standard
                ? 20 +
                  (sensor.station && /^[ABC]$/.test(sensor.station)
                    ? sensor.station.charCodeAt(0) - 65
                    : index) *
                    14
                : 15 + (index * 38) / Math.max(1, sensores.length - 1)
            }
            cy={17}
            r={5.5}
            fill={colorSensor(sensor, tempMinOk, tempMaxOk)}
            stroke="#fff"
            strokeWidth={1.5}
          />
        ))}
      </svg>
    );
  }
  const geometry = compacta ? COMPACT : FULL;
  const viewBox = compacta ? COMPACT.viewBox : '0 0 220 120';
  const strokeWidth = compacta ? 1.5 : 2;
  const sensorRadius = compacta ? 3 : 5;

  return (
    <div className="flex flex-col items-center">
      <svg
        viewBox={viewBox}
        preserveAspectRatio={compacta ? 'none' : undefined}
        className={compacta ? 'mt-0.5 h-16 w-full' : 'h-28 w-full max-w-[220px]'}
        role="img"
        aria-label="Silueta lateral de una nave con cubierta a dos aguas"
      >
        {/* Tejado bajo: alero, pendiente izquierda, cumbrera y cierre
            corto a la derecha. No es un triángulo frontal simétrico. */}
        <path
          d={
            compacta
              ? `M${geometry.wallLeft} ${geometry.eaveY} L${geometry.ridgeLeft} ${geometry.ridgeY} L${(geometry.wallRight + geometry.endRight) / 2} ${geometry.ridgeY} L${geometry.wallRight} ${geometry.eaveY} Z`
              : `M${geometry.wallLeft} ${geometry.eaveY} L${geometry.ridgeLeft} ${geometry.ridgeY} L${geometry.ridgeRight} ${geometry.ridgeY} L${geometry.endRight} ${geometry.eaveY} Z`
          }
          fill="var(--c-surface)"
          stroke="var(--c-border-strong)"
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />
        {!compacta && (
          <line
            x1={geometry.ridgeLeft}
            y1={geometry.ridgeY}
            x2={geometry.ridgeRight}
            y2={geometry.ridgeY}
            stroke="var(--c-text-primary)"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        )}

        {/* Testero superior: triángulo equilátero independiente,
            con base en el alero y vértice centrado. */}
        {compacta && (
          <path
            d={`M${geometry.wallRight} ${geometry.eaveY} L${geometry.endRight} ${geometry.eaveY} L${(geometry.wallRight + geometry.endRight) / 2} ${geometry.ridgeY} Z`}
            fill="var(--c-surface)"
            stroke="var(--c-border-strong)"
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
          />
        )}

        {/* Pared lateral principal, baja y recta. */}
        <path
          d={`M${geometry.wallLeft} ${geometry.eaveY} L${geometry.wallRight} ${geometry.eaveY} L${geometry.wallRight} ${geometry.floorY} L${geometry.wallLeft} ${geometry.floorY} Z`}
          fill="var(--c-surface)"
          stroke="var(--c-border-strong)"
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />

        {/* Testero estrecho del fondo: refuerza la lectura de dos aguas
            sin hacer que toda la nave parezca una fachada. */}
        <path
          d={`M${geometry.wallRight} ${geometry.eaveY} L${geometry.endRight} ${geometry.eaveY} L${geometry.endRight} ${geometry.floorY} L${geometry.wallRight} ${geometry.floorY} Z`}
          fill="var(--c-surface)"
          stroke="var(--c-border-strong)"
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />

        {/* Sensores encima de las paredes para que líneas y puntos queden
            visibles y conecten visualmente con las lecturas superiores. */}
        {sensores.map((sensor, index) => {
          const color = colorSensor(sensor, tempMinOk, tempMaxOk);
          const slot =
            sensor.station && /^[ABC]$/.test(sensor.station)
              ? sensor.station.charCodeAt(0) - 65
              : index;
          const x = standard
            ? geometry.sensorX[slot]
            : geometry.sensorX[0] +
              (index * (geometry.sensorX[2] - geometry.sensorX[0])) /
                Math.max(1, sensores.length - 1);
          return (
            <g key={`sensor-${index}`}>
              <line
                x1={x}
                y1={compacta ? 2 : geometry.eaveY}
                x2={x}
                y2={geometry.sensorY - sensorRadius - 1}
                stroke={color}
                strokeWidth={compacta ? 0.75 : strokeWidth}
              />
              <circle cx={x} cy={geometry.sensorY} r={sensorRadius} fill={color} />
            </g>
          );
        })}
      </svg>

      {/* En la Home el padre coloca este bloque arriba mediante order-first.
          Las columnas tienen los mismos tres ejes relativos que los sensores. */}
      {!mini && (
        <div
          className={
            compacta
              ? 'order-first mt-0.5 grid w-full grid-cols-3 items-start'
              : 'mt-1 flex w-full max-w-[220px] items-start justify-between px-2'
          }
        >
          {sensores.map((sensor, index) => {
            const alarmaTemp =
              sensor.tempState !== 'disconnected' &&
              sensor.tempState !== 'no_data' &&
              (sensor.tempState === 'alert' ||
                (sensor.temp != null && (sensor.temp < tempMinOk || sensor.temp > tempMaxOk)));
            const alarmaHum = sensor.humState === 'alert';
            const claseTemp = alarmaTemp
              ? 'text-warning-text'
              : sensor.temp != null &&
                  sensor.tempState !== 'disconnected' &&
                  sensor.tempState !== 'no_data'
                ? 'text-text-primary'
                : 'text-text-muted';
            const claseHum = alarmaHum
              ? 'text-warning-text'
              : sensor.hum != null &&
                  sensor.humState !== 'disconnected' &&
                  sensor.humState !== 'no_data'
                ? 'text-text-primary'
                : 'text-text-muted';
            return (
              <div key={`reading-${index}`} className="flex flex-col items-center">
                <span className={`text-[11px] font-medium leading-3 tabular-nums ${claseTemp}`}>
                  {sensor.temp != null ? `${fmt1(sensor.temp)}°` : '—'}
                </span>
                <span className={`text-[10px] leading-3 tabular-nums ${claseHum}`}>
                  {sensor.hum != null ? `${Math.round(sensor.hum)}%` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
